import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "./../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Cloud, RefreshCw, Trash2, ExternalLink, AlertCircle, CheckCircle2, BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function IntegrationsTab({ pid, reload }) {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/programs/${pid}/integrations`);
      setIntegrations(data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  const byType = (t) => integrations.find((i) => i.type === t);

  return (
    <div className="space-y-6" data-testid="integrations-tab">
      <div className="bg-slate-900 text-white p-5 rounded-sm flex items-start gap-3">
        <Cloud className="w-5 h-5 mt-0.5 shrink-0" />
        <div>
          <p className="label-overline text-slate-400">External data sources</p>
          <h3 className="font-heading text-xl font-semibold mt-1">Pull submissions automatically</h3>
          <p className="text-sm text-slate-300 mt-1 max-w-2xl">
            Connect a KoBoToolbox asset or an ODK Central form. Each "Sync now" pulls the latest CSV
            and queues it as a regular upload — you map columns once and every future sync routes
            through the same pipeline.
          </p>
          <Link to={`/programs/${pid}/theory-of-change`} className="hidden">{/* keeps router import warm */}</Link>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <KoboCard pid={pid} integration={byType("kobo")} reload={() => { load(); reload?.(); }} />
          <OdkCard pid={pid} integration={byType("odk")} reload={() => { load(); reload?.(); }} />
        </div>
      )}
    </div>
  );
}

function StatusLine({ integration }) {
  if (!integration) {
    return <p className="text-xs text-slate-500 mt-2">Not configured.</p>;
  }
  const ok = integration.last_synced_at && !integration.last_sync_error;
  const err = integration.last_sync_error;
  return (
    <div className="text-xs mt-2 space-y-1">
      <p className="text-slate-500">
        Configured by {integration.created_by_name || "?"}
      </p>
      {ok && (
        <p className="text-emerald-700 inline-flex items-center gap-1" data-testid={`integration-ok-${integration.type}`}>
          <CheckCircle2 className="w-3 h-3" /> Last sync {new Date(integration.last_synced_at).toLocaleString()}
        </p>
      )}
      {err && (
        <p className="text-red-700 inline-flex items-center gap-1" data-testid={`integration-err-${integration.type}`}>
          <AlertCircle className="w-3 h-3" /> {err}
        </p>
      )}
    </div>
  );
}

function KoboCard({ pid, integration, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ server: "https://kf.kobotoolbox.org", asset_uid: "", api_token: "" });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/programs/${pid}/integrations/kobo`, form);
      toast.success("KoBoToolbox connected");
      setOpen(false);
      setForm({ server: "https://kf.kobotoolbox.org", asset_uid: "", api_token: "" });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/programs/${pid}/integrations/kobo/sync`);
      toast.success(`Pulled ${data.row_count} rows from KoBoToolbox`);
      navigate(`/programs/${pid}/uploads/${data.id}/validate`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
      reload();
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm("Remove this KoBoToolbox connection? Saved credentials will be deleted.")) return;
    await api.delete(`/programs/${pid}/integrations/kobo`);
    toast.success("KoBoToolbox connection removed");
    reload();
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="integration-kobo-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="label-overline">KoBoToolbox</p>
          <h4 className="font-heading text-lg font-semibold mt-1">Asset CSV pull</h4>
        </div>
        <a href="https://www.kobotoolbox.org/" target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:text-slate-700" title="Open KoBoToolbox documentation">
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>

      {integration ? (
        <div className="text-sm space-y-2">
          <p className="text-slate-700">
            <span className="text-slate-500">Server:</span> <span className="font-mono text-xs">{integration.config.server}</span>
          </p>
          <p className="text-slate-700">
            <span className="text-slate-500">Asset:</span> <span className="font-mono text-xs">{integration.config.asset_uid}</span>
          </p>
          <p className="text-slate-700">
            <span className="text-slate-500">Token:</span> <span className="font-mono text-xs">{integration.config.api_token}</span>
          </p>
          <StatusLine integration={integration} />
          <div className="flex gap-2 pt-2">
            <Button onClick={sync} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="kobo-sync">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Sync now
            </Button>
            <Button variant="outline" onClick={() => setOpen(true)} disabled={busy} data-testid="kobo-edit">Update credentials</Button>
            <Button variant="outline" onClick={remove} disabled={busy} className="text-red-700 border-red-200 hover:bg-red-50" data-testid="kobo-remove">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      ) : (
        <Button onClick={() => setOpen(true)} variant="outline" data-testid="kobo-connect" className="w-full justify-center">
          Connect KoBoToolbox
        </Button>
      )}

      {open && (
        <div className="mt-4 space-y-3 border-t border-slate-100 pt-4">
          <div>
            <Label className="label-overline">Server (optional)</Label>
            <Input value={form.server} onChange={(e) => setForm({ ...form, server: e.target.value })} className="mt-1 font-mono text-xs" placeholder="https://kf.kobotoolbox.org" data-testid="kobo-server"/>
          </div>
          <div>
            <Label className="label-overline">Asset UID</Label>
            <Input value={form.asset_uid} onChange={(e) => setForm({ ...form, asset_uid: e.target.value })} className="mt-1 font-mono text-xs" placeholder="aZxYwVu123…" data-testid="kobo-asset"/>
            <p className="text-[10px] text-slate-500 mt-1">Find it in your form's URL after `/forms/`.</p>
          </div>
          <div>
            <Label className="label-overline">API token</Label>
            <Input type="password" value={form.api_token} onChange={(e) => setForm({ ...form, api_token: e.target.value })} className="mt-1 font-mono text-xs" placeholder="xxxxxxxxxxxx" data-testid="kobo-token"/>
            <p className="text-[10px] text-slate-500 mt-1">
              <BookOpen className="w-2.5 h-2.5 inline mr-0.5" />
              Account &rarr; My Account &rarr; API token. Stored encrypted at rest.
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={save} disabled={busy || !form.asset_uid || !form.api_token} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="kobo-save">
              {busy ? "Saving…" : "Save"}
            </Button>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function OdkCard({ pid, integration, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ server: "", project_id: "", form_id: "", username: "", password: "" });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/programs/${pid}/integrations/odk`, form);
      toast.success("ODK Central connected");
      setOpen(false);
      setForm({ server: "", project_id: "", form_id: "", username: "", password: "" });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/programs/${pid}/integrations/odk/sync`);
      toast.success(`Pulled ${data.row_count} submissions from ODK Central`);
      navigate(`/programs/${pid}/uploads/${data.id}/validate`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
      reload();
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm("Remove this ODK Central connection? Saved credentials will be deleted.")) return;
    await api.delete(`/programs/${pid}/integrations/odk`);
    toast.success("ODK Central connection removed");
    reload();
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="integration-odk-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="label-overline">ODK Central</p>
          <h4 className="font-heading text-lg font-semibold mt-1">Form submissions CSV</h4>
        </div>
        <a href="https://docs.getodk.org/central-intro/" target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:text-slate-700" title="Open ODK Central documentation">
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>

      {integration ? (
        <div className="text-sm space-y-2">
          <p className="text-slate-700"><span className="text-slate-500">Server:</span> <span className="font-mono text-xs">{integration.config.server}</span></p>
          <p className="text-slate-700"><span className="text-slate-500">Project:</span> <span className="font-mono text-xs">{integration.config.project_id}</span></p>
          <p className="text-slate-700"><span className="text-slate-500">Form:</span> <span className="font-mono text-xs">{integration.config.form_id}</span></p>
          <p className="text-slate-700"><span className="text-slate-500">User:</span> <span className="font-mono text-xs">{integration.config.username}</span></p>
          <StatusLine integration={integration} />
          <div className="flex gap-2 pt-2">
            <Button onClick={sync} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="odk-sync">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Sync now
            </Button>
            <Button variant="outline" onClick={() => setOpen(true)} disabled={busy} data-testid="odk-edit">Update credentials</Button>
            <Button variant="outline" onClick={remove} disabled={busy} className="text-red-700 border-red-200 hover:bg-red-50" data-testid="odk-remove">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      ) : (
        <Button onClick={() => setOpen(true)} variant="outline" data-testid="odk-connect" className="w-full justify-center">
          Connect ODK Central
        </Button>
      )}

      {open && (
        <div className="mt-4 space-y-3 border-t border-slate-100 pt-4">
          <div>
            <Label className="label-overline">Server URL</Label>
            <Input value={form.server} onChange={(e) => setForm({ ...form, server: e.target.value })} className="mt-1 font-mono text-xs" placeholder="https://central.example.com" data-testid="odk-server"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-overline">Project ID</Label>
              <Input value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="mt-1 font-mono text-xs" placeholder="3" data-testid="odk-project"/>
            </div>
            <div>
              <Label className="label-overline">Form ID</Label>
              <Input value={form.form_id} onChange={(e) => setForm({ ...form, form_id: e.target.value })} className="mt-1 font-mono text-xs" placeholder="my_form" data-testid="odk-form"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-overline">Username (email)</Label>
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1 text-xs" placeholder="user@example.com" data-testid="odk-username"/>
            </div>
            <div>
              <Label className="label-overline">Password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 font-mono text-xs" placeholder="••••••••" data-testid="odk-password"/>
            </div>
          </div>
          <p className="text-[10px] text-slate-500">Stored encrypted at rest.</p>
          <div className="flex gap-2">
            <Button onClick={save} disabled={busy || !form.server || !form.project_id || !form.form_id || !form.username || !form.password} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="odk-save">
              {busy ? "Saving…" : "Save"}
            </Button>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
